import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UserDataService } from '../../services/core/user-data.service';
import { Router } from '@angular/router';
import { HttpService } from 'src/app/services/core/http.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MapSocketService } from 'src/app/services/core/map-socket.service';
import { RouteGuardsService } from 'src/app/services/core/route-guards.service';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { AmplitudeService } from 'src/app/services/core/amplitude.service';
import { getRelacionamentoCores } from 'src/app/shared/constants/planta-relationship';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { NgClass, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIconButton } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { LoaderComponent } from '../loader/loader.component';

// ugm, TRIAL, POC, Outros
const SPECIAL_ID_CLIENTES = [1, 2, 3, 4, 5, 99];
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatToolbar,
    MatTooltip,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    NgClass,
    AsyncPipe,
    TranslateModule,
    FormsModule,
    MatInput,
    MatFormField,
    MatPrefix,
    MatSuffix,
    MatIconButton,
    MatBadgeModule,
    LoaderComponent,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  user = inject(UserDataService);
  private http = inject(HttpService);
  translate = inject(TranslateService);
  private socketCentral = inject(MapSocketService);
  routeGuards = inject(RouteGuardsService);
  private amplitude = inject(AmplitudeService);

  public logo = '';
  public version = environment.version;
  public isDev = !environment.production;
  public clientMenu = signal<{ key: string; value: any[] }[]>([]);
  private routeSubscription: Subscription;
  public displayedClientePlantaName = computed(() => this.getDisplayedClientePlantaName());
  public showUsersButton = computed(
    () =>
      this.user.isAdmin() ||
      this.user.isManager() ||
      this.user.hasRecipientSubscriptionOption() ||
      this.user.hasCamDetection() ||
      this.user.hasSatellite() ||
      this.user.hasFireManagement()
  );

  private _searchFilterUgm = signal('');
  private _searchFilterManager = signal('');
  public isRedirecting = signal(false);

  @ViewChild('searchInput') searchInputInput: ElementRef;

  async ngOnInit() {
    this.routeSubscription = this.routeGuards.authorized.subscribe(async (authorized) => {
      if (authorized) {
        this.amplitude.setUserProperties();
        if (this.user.isUgm()) {
          this.user.plantas = await this.http.maestroUserGet('v2/get_plantas_safe');

          const clientMenuObj = {};
          new Set(this.user.plantas.map((p) => p.nome_cliente)).forEach((cliente) => {
            clientMenuObj[`${cliente}`] = this.user.plantas
              .filter((p) => p.nome_cliente === cliente)
              .sort((p1, p2) =>
                this.normalizeText(p1.nome_planta).localeCompare(
                  this.normalizeText(p2.nome_planta),
                  'pt-BR',
                  { sensitivity: 'base' }
                )
              );
          });

          this.clientMenu.set(
            Object.keys(clientMenuObj)
              .map((key) => ({ key, value: clientMenuObj[key] }))
              .sort((a, b) => {
                const nomeA = a.value.length === 1 ? a.value[0].nome_cliente : a.key;
                const nomeB = b.value.length === 1 ? b.value[0].nome_cliente : b.key;

                return this.normalizeText(nomeA || '').localeCompare(
                  this.normalizeText(nomeB || ''),
                  'pt-BR',
                  { sensitivity: 'base' }
                );
              })
          );
        }
      }
    });
  }

  getDisplayedClientePlantaName() {
    if (this.user.isUgm() && this.routeGuards.authorized) {
      if (this.user.getNomeCliente() !== this.user.getNomePlanta()) {
        return `${this.user.getNomeCliente()} - ${this.user.getNomePlanta()}`;
      } else {
        return `${this.user.getNomeCliente()}`;
      }
    }

    if (!this.user.isUgm() && this.routeGuards.authorized) {
      if (!SPECIAL_ID_CLIENTES.includes(this.user.getIdCliente())) {
        return `${this.user.getNomeCliente()}`;
      } else {
        return `${this.user.getNomePlanta()}`;
      }
    }

    return '';
  }

  async changePlanta(newPlanta) {
    if (this.user.hasSatellite()) {
      this.socketCentral.disconnectFromSockCent();
    }
    try {
      await this.user.changePlantaToken(newPlanta.id_planta);
    } catch {
      return;
    }
    this.reloadWithoutParams();
  }

  reload() {
    const activeUrl = this.router.url;
    this.router
      .navigateByUrl('/home', { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(activeUrl));
  }

  reloadWithoutParams() {
    const activeUrl = this.router.url;
    const pathWithoutParams = activeUrl.split('?')[0];
    this.router
      .navigateByUrl('/home', { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(pathWithoutParams));
  }

  ngOnDestroy() {
    this.routeSubscription.unsubscribe();
  }

  handleImageError(event: any) {
    const imagemPadrao = `${this.http.getServerUrl}/get_logo/999`;
    event.target.src = imagemPadrao;
  }

  async redirectIfAdmin(): Promise<void> {
    if (!this.user.isAdmin()) return;

    const currentRoute = this.router.url;
    this.isRedirecting.set(true);
    try {
      if (currentRoute.includes('map')) {
        // window.open('/#/admin', '_blank');
        await this.router.navigate(['/admin']);
      } else {
        await this.router.navigate(['/map']);
      }
    } finally {
      this.isRedirecting.set(false);
    }
  }

  // Filtrar menu de clientes/plantas para UGM
  getFilteredClientMenu = computed(() => {
    const filter = this.normalizeText(this._searchFilterUgm());

    if (!filter) {
      return this.clientMenu();
    }

    return this.clientMenu()
      .map((client) => {
        const filteredPlantas = client.value.filter(
          (planta) =>
            (planta.nome_cliente && this.normalizeText(planta.nome_cliente).includes(filter)) ||
            (planta.nome_planta && this.normalizeText(planta.nome_planta).includes(filter)) ||
            (planta.id_planta && planta.id_planta.toString().includes(filter))
        );

        filteredPlantas.sort((p1, p2) =>
          this.normalizeText(p1.nome_planta || '').localeCompare(
            this.normalizeText(p2.nome_planta || ''),
            'pt-BR',
            {
              sensitivity: 'base',
            }
          )
        );

        return { key: client.key, value: filteredPlantas };
      })
      .filter((client) => client.value.length > 0)
      .sort((a, b) => {
        const nomeA = a.value.length === 1 ? a.value[0].nome_cliente : a.key;
        const nomeB = b.value.length === 1 ? b.value[0].nome_cliente : b.key;

        return this.normalizeText(nomeA || '').localeCompare(
          this.normalizeText(nomeB || ''),
          'pt-BR',
          { sensitivity: 'base' }
        );
      });
  });

  // Filtrar plantas para gestor nível 2
  getFilteredPlantasList(): any[] {
    const filter = this.normalizeText(this._searchFilterManager());
    if (!filter) {
      return this.user.plantas_list || [];
    }

    return (this.user.plantas_list || []).filter(
      (planta) =>
        this.normalizeText(planta.nome_planta).includes(filter) ||
        (planta.nome_cliente && this.normalizeText(planta.nome_cliente).includes(filter)) ||
        (planta.id_planta && planta.id_planta.toString().includes(filter))
    );
  }

  get searchFilterUgm(): string {
    return this._searchFilterUgm();
  }

  set searchFilterUgm(value: string) {
    this._searchFilterUgm.set(value);
  }

  get searchFilterManager(): string {
    return this._searchFilterManager();
  }

  set searchFilterManager(value: string) {
    this._searchFilterManager.set(value);
  }

  // Limpar filtro UGM
  clearSearchUgm(): void {
    this._searchFilterUgm.set('');
  }

  // Limpar filtro Manager
  clearSearchManager(): void {
    this._searchFilterManager.set('');
  }

  focusOnSearch() {
    setTimeout(() => {
      if (this.searchInputInput) {
        this.searchInputInput.nativeElement.focus();
      }
    }, 100);
  }

  isClientSelected(planta: any[]): boolean {
    return planta && planta.some((p) => p.id_planta === this.user.getIdPlanta());
  }

  getRelacionamentoCores(idTipoRelacionamento: number) {
    return getRelacionamentoCores(idTipoRelacionamento);
  }

  private normalizeText(value: string): string {
    return value
      ?.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  openSettings(): void {
    this.user.openSettings();
  }

  openUsers(): void {
    this.user.openUsers();
  }

  openInterface(): void {
    if (!this.user.isSupportEdge()) return;
    window.open('/#/interface', '_blank');
  }
}
